import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Empty } from 'antd';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import DashboardCard from './DashboardCard';

/**
 * Notifications card for the new talent dashboard.
 * Functional / Tailwind port of MessagesPanel.
 */

const NOTIFICATIONS_PATH = '/talent/notifications';
const MAX_NOTIFICATIONS = 2;

// Compact relative time: "now", "5m", "3h", "22d", "3mo", "1y"
const shortTimeAgo = (date) => {
    const then = TB.moment(date);
    if (!then.isValid()) return '';

    const now = TB.moment();
    const units = [['y', 'years'], ['mo', 'months'], ['d', 'days'], ['h', 'hours'], ['m', 'minutes']];

    for (const [suffix, unit] of units) {
        const value = now.diff(then, unit);
        if (value >= 1) return `${value}${suffix}`;
    }
    return 'now';
};

const stripHtml = (html = '') => String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');

const NotificationItem = ({ notification }) => {
    const isUnread = !notification.read_at;

    return (
        <Link
            to={NOTIFICATIONS_PATH}
            state={{ selectedMessageId: notification.id }}
            className="flex gap-2 rounded-xl border border-solid border-gray-100 bg-gray-50 p-3 text-gray-900 hover:border-primary/30 hover:bg-primary/5 hover:text-gray-900 hover:no-underline"
        >
            <span className="w-2 shrink-0 pt-1.5" aria-hidden="true">
                {isUnread && <span className="block h-2 w-2 rounded-full bg-primary-lighter" />}
            </span>

            <span className="min-w-0 flex-1">
                <span className="mb-1 flex items-start justify-between gap-3">
                    <span
                        className={TB.mapClass('line-clamp-2 text-sm leading-snug', {
                            'font-semibold text-gray-900': isUnread,
                            'font-medium text-gray-700': !isUnread,
                        })}
                    >
                        {notification.title}
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-xs text-gray-400" title={TB.moment(notification.created_at).format('lll')}>
                        {shortTimeAgo(notification.created_at)}
                    </span>
                </span>
                <span className="line-clamp-2 text-xs text-gray-500">
                    {stripHtml(notification.body)}
                </span>
            </span>
        </Link>
    );
};

// Newest first, capped to the dashboard limit
const latestNotifications = (notifications = [], limit = MAX_NOTIFICATIONS) => (
    TB.orderBy(notifications || [], notification => TB.moment(notification.created_at).valueOf(), 'desc').slice(0, limit)
);

const NotificationsCard = ({ isLoading, notifications, totalUnread, className }) => {
    const latest = latestNotifications(notifications);

    return (
    <DashboardCard
        title="Notifications"
        count={totalUnread}
        className={className}
        bodyClassName="flex flex-col"
        headerRight={!isLoading && (
            <Link
                to={NOTIFICATIONS_PATH}
                className="whitespace-nowrap text-sm font-semibold text-primary hover:text-primary-dark hover:no-underline"
            >
                View all
            </Link>
        )}
    >
        {isLoading ? (
            <div className="flex flex-1 items-center justify-center py-8">
                <LoadingSpinner size="lg" />
            </div>
        ) : latest.length ? (
            <div className="flex flex-col gap-3">
                {latest.map(notification => (
                    <NotificationItem key={notification.id} notification={notification} />
                ))}
            </div>
        ) : (
            <Empty
                className="m-0 flex flex-1 flex-col items-center justify-center py-6"
                image="/images/heads.png"
                imageStyle={{ height: 60 }}
                description={(
                    <span className="flex flex-col items-center gap-1">
                        <span className="text-base font-semibold text-gray-900">No notifications yet</span>
                        <span className="text-sm text-gray-500">Notifications will appear here.</span>
                    </span>
                )}
            />
        )}
    </DashboardCard>
    );
};

NotificationsCard.propTypes = {
    isLoading: PropTypes.bool,
    notifications: PropTypes.array,
    totalUnread: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    className: PropTypes.string,
};

export default NotificationsCard;
